import { intl } from "LocaleWrapper";
import { useApolloClient, useMutation } from "@apollo/client";
import { QuickFilterTypes, Table } from "@cofano/cck";
import DeleteIcon from "@mui/icons-material/Delete";

import { TableId, useBackendTable } from "components/table";
import { useConfirm } from "components/hooks/useConfirm";

import CharterTypeTag from "view/charters/components/tags/CharterTypeTag";
import CharterTypeSelect from "view/charters/components/selects/CharterTypeSelect.tsx";
import CharterSubtypeSelect from "view/charters/components/selects/CharterSubtypeSelect";
import CharterSubtypeTag from "view/charters/components/tags/CharterSubtypeTag";

import { ChildPropsFromPagination } from "types/commons";

import { CharterSubtype, SortDirection } from "__generated__/enums";
import { GetAgreementTemplatesDocument, GetAgreementTemplatesQuery } from "__generated__/view/templates/graphql/template.queries.generated";
import { DeleteAgreementTemplateDocument } from "__generated__/view/templates/graphql/template.mutations.generated";

type TemplateFromQuery = ChildPropsFromPagination<GetAgreementTemplatesQuery, "agreementTemplates">;

const TemplateAgreementTable = () => {

	const confirm = useConfirm();
	const client = useApolloClient();

	const [ deleteAgreementTemplate ] = useMutation(DeleteAgreementTemplateDocument);

	const { tableProps, refetchAfterDelete } = useBackendTable({
		id: TableId.TemplateAgreements,
		onChange: async (pagination, sort, filter, callback) => {
			try {
				const result = await client.query({
					query: GetAgreementTemplatesDocument,
					variables: {
						pagination: {
							...pagination,
							sortList: sort ?? [ {
								field: "id",
								direction: SortDirection.Asc,
							} ],
						},
						search: {
							description: filter.description,
							charterTypes: filter.compatibleCharterType,
							charterSubtypes: filter.compatibleCharterSubtype,
						},
					},
				});

				callback({
					content: result.data.agreementTemplates?.content ?? [],
					totalResults: result.data.agreementTemplates?.totalElements ?? 0,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	const deleteTemplate = (id: number) => {
		deleteAgreementTemplate({
			variables: {
				id: id,
			},
		}).then(() => refetchAfterDelete());
	};

	return (
		<Table
			{...tableProps}
			columns={[ {
				id: "description",
				title: intl.formatMessage({ id: "DESCRIPTION" }),
				sortable: true,
				filterComponent: QuickFilterTypes.TEXT,
			}, {
				id: "compatibleCharterType",
				title: intl.formatMessage({ id: "TYPE" }),
				render: (row: TemplateFromQuery) => <CharterTypeTag type={row.compatibleCharterType} />,
				sortable: true,
				filterComponent: (value, onChange) => (
					<CharterTypeSelect
						value={value}
						label={intl.formatMessage({ id: "CHARTER_TYPE" })}
						onChange={options => onChange(options)}
						multiple
					/>
				),
			}, {
				id: "compatibleCharterSubtype",
				title: intl.formatMessage({ id: "SUBTYPE" }),
				render: (row: TemplateFromQuery) => <CharterSubtypeTag type={row.compatibleCharterSubtype} />,
				sortable: true,
				filterComponent: (value, onChange) => (
					<CharterSubtypeSelect
						value={value}
						label={intl.formatMessage({ id: "SUBTYPE" })}
						onChange={options => onChange(options)}
						customCharterSubtypes={Object.values(CharterSubtype)}
						multiple
					/>
				),
			} ]}
			rowActions={[ {
				icon: <DeleteIcon />,
				onClick: (row) => {
					confirm({
						type: "DELETE",
					}).then(() => {
						deleteTemplate(row.id);
					});
				},
			} ]} />
	);
};

export default TemplateAgreementTable;
